/**
 * National IT review (national_reviewer only). A queue of the ratings firms sent to National IT, and one submission
 * beside its evidence, the assistant's original draft and the CDW 2026 result. Accepting records that
 * National IT has checked the evidence; the firm keeps ownership of its status, decisions and risk.
 */
import { areaName, CDW_GAP_STATUS, resultLabel, SUBMISSION_STATUS } from '@grc/shared/vocabulary';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, useApi, useApiClient, useDownload } from '../api';
import { Card, formatDate, Load, PageBack, PageHeader, Status } from '../components';
import { useMeContext } from '../Layout';
import { CRITERION_RESULT, DoesNotSupportList, type Draft } from './AssistantPanel';

interface QueueRow {
  id: number;
  firmCode: string;
  firmName: string;
  controlId: string;
  controlTitle: string;
  domainNumber: string;
  status: string;
  proposedStatus: string;
  proposedMaturity: number | null;
  cdwStatus: string | null;
  cdwMaturity: number | null;
  submittedBy: string;
  submittedAt: string;
  hasAiDraft: boolean;
}

interface Detail {
  id: number;
  firmCode: string;
  firmName: string;
  controlId: string;
  controlTitle: string;
  controlDescription: string;
  status: string;
  proposedStatus: string;
  proposedMaturity: number | null;
  implementation: string;
  evidenceMapping: Draft['criteria'];
  remainingGaps: string[];
  aiDraft: Draft | null;
  aiModel: string | null;
  submittedBy: string;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  isOwnSubmission: boolean;
  cdw: null | { status: string; maturity: number | null; observedState: string | null; gap: string | null; recommendation: string | null };
  files: Array<{ id: number; fileName: string; sizeBytes: number; scanStatus: string; uploadedAt: string }>;
  history: Array<{ id: number; status: string; submittedAt: string; reviewedBy: string | null; reviewNote: string | null }>;
  conversation: Array<{ role: 'user' | 'assistant'; content: string; createdAt: string }>;
}

const statusText = (k: string | null) => (k ? resultLabel(k) : 'Not assessed');

const QUEUE_STATUSES = ['submitted', 'returned', 'accepted', 'all'];

/**
 * Where the review queue lives for this reader (5D B, DEC-49): National IT reviewers have it as the National view's
 * Reviews tab; a reviewer outside National IT keeps the separate page, exactly as before. Until the reader is known,
 * the separate page, whose route sends National IT on to the tab.
 */
export function useQueueHome(): string {
  const me = useMeContext();
  // CP-08: the queue's filter travels with a submission (?status=), so going back, or deciding, returns to it.
  const [params] = useSearchParams();
  const status = params.get('status');
  const filter = status && status !== 'submitted' && QUEUE_STATUSES.includes(status) ? status : null;
  const home = me.data?.tiers.national ? '/national?view=reviews' : '/review';
  return filter ? `${home}${home.includes('?') ? '&' : '?'}status=${filter}` : home;
}

/**
 * The old review page's address (/review, with its ?status=) for National IT lands on the National view's Reviews
 * tab with the same filter; for anyone else it is the page it always was, behind the same API guard (assertReviewer).
 */
export function ReviewRoute() {
  const me = useMeContext();
  const [params] = useSearchParams();
  return (
    <Load state={me} what="your access">
      {(m) => {
        if (!m.tiers.national) return <ReviewQueue />;
        const status = params.get('status');
        return <Navigate to={`/national?view=reviews${status && QUEUE_STATUSES.includes(status) ? `&status=${status}` : ''}`} replace />;
      }}
    </Load>
  );
}

/** The separate review page, for a reviewer outside National IT. */
export function ReviewQueue() {
  return (
    <>
      <PageBack to="/">Back to Home</PageBack>
      <PageHeader title="Ratings to review" lede="Ratings firms sent to National IT, waiting for a check of the evidence. The firm owns the result." />
      <ReviewQueueTable />
    </>
  );
}

/** The queue with its filter; the filter is in the address (?status=), so a link or a reload keeps it. */
export function ReviewQueueTable() {
  const [params, setParams] = useSearchParams();
  const asked = params.get('status');
  const status = asked && QUEUE_STATUSES.includes(asked) ? asked : 'submitted';
  const setStatus = (s: string) => {
    const next = new URLSearchParams(window.location.search);
    if (s === 'submitted') next.delete('status');
    else next.set('status', s);
    setParams(next, { replace: true });
  };
  const state = useApi<{ submissions: QueueRow[] }>(`/review/submissions?status=${status}`);
  return (
    <>
      <div className="filters">
        <label>
          Show
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="submitted">Waiting for review</option>
            <option value="returned">Returned with questions</option>
            <option value="accepted">Reviewed</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>
      <Load state={state} what="the review queue">
        {({ submissions }) =>
          submissions.length ? (
            <div className="table-wrap" tabIndex={0} role="region" aria-label="Review queue">
              <table>
                <caption className="sr-only">Ratings to review, {submissions.length} shown</caption>
                <thead>
                  <tr>
                    <th scope="col">Firm</th>
                    <th scope="col">Control</th>
                    <th scope="col">Firm proposes</th>
                    <th scope="col">CDW 2026</th>
                    <th scope="col">Submitted</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id}>
                      <th scope="row">{s.firmName}</th>
                      <td>
                        <Link to={`/review/${s.id}${status === 'submitted' ? '' : `?status=${status}`}`}>
                          <span className="cid">{s.controlId}</span> {s.controlTitle}
                        </Link>{' '}
                        <span className="muted small">{areaName(s.domainNumber)}</span>
                      </td>
                      <td>
                        {statusText(s.proposedStatus)}
                        {s.proposedMaturity !== null && `, maturity ${s.proposedMaturity}`}
                      </td>
                      <td>
                        {statusText(s.cdwStatus)}
                        {s.cdwMaturity !== null && `, maturity ${s.cdwMaturity}`}
                      </td>
                      <td>
                        {formatDate(s.submittedAt)} <span className="muted small">{s.submittedBy}</span>
                      </td>
                      <td>
                        <Status map={SUBMISSION_STATUS} value={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Nothing here.</p>
          )
        }
      </Load>
    </>
  );
}

export function ReviewSubmission() {
  const { submissionId } = useParams();
  const state = useApi<Detail>(`/review/submissions/${submissionId}`);
  const download = useDownload();
  const home = useQueueHome();
  return (
    <Load state={state} what="the submission">
      {(d) => (
        <>
          <p className="page-back">
            <Link to={home}>All submissions</Link>
          </p>
          <PageHeader eyebrow={`Control ${d.controlId} at ${d.firmName}`} title={d.controlTitle} lede={<Status map={SUBMISSION_STATUS} value={d.status} />} />
          <div className="cards two">
            <Card title="What the firm submitted">
              <p>
                <strong>{statusText(d.proposedStatus)}</strong>
                {d.proposedMaturity !== null && <>, maturity {d.proposedMaturity}</>}{' '}
                <span className="muted small">
                  by {d.submittedBy}, {formatDate(d.submittedAt)}
                </span>
              </p>
              <p className="pre">{d.implementation}</p>
              {d.remainingGaps.length > 0 && (
                <>
                  <h3>Still missing, by the firm's account</h3>
                  <ul>
                    {d.remainingGaps.map((g, i) => (
                      <li key={i}>{g}</li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
            <Card title="CDW 2026 assessment">
              {d.cdw ? (
                <>
                  <p>
                    <Status map={CDW_GAP_STATUS} value={d.cdw.status} />
                    {d.cdw.maturity !== null && <span className="muted small"> maturity {d.cdw.maturity}</span>}
                  </p>
                  {d.cdw.gap && <p className="pre">{d.cdw.gap}</p>}
                  {d.cdw.recommendation && (
                    <>
                      <h3>Recommendation</h3>
                      <p className="pre">{d.cdw.recommendation}</p>
                    </>
                  )}
                </>
              ) : (
                <p className="muted">No CDW result for this control.</p>
              )}
            </Card>
          </div>

          <Card title="Evidence">
            {d.files.some((f) => f.scanStatus !== 'clean') && (
              <p className="notice small">Not every file has been scanned clean for malware. Open them with care, and check the scan before accepting.</p>
            )}
            {d.files.length ? (
              <ul className="plain files">
                {d.files.map((f) => (
                  <li key={f.id}>
                    <button className="link" onClick={() => download(`/review/submissions/${d.id}/evidence/${f.id}`, f.fileName)}>
                      #{f.id} {f.fileName}
                    </button>{' '}
                    <span className="muted small">
                      {Math.ceil(f.sizeBytes / 1024)} KB, {formatDate(f.uploadedAt)}
                      {f.scanStatus !== 'clean' && `, ${SCAN_TEXT[f.scanStatus] ?? 'not scanned clean'}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No files uploaded.</p>
            )}
            {d.evidenceMapping.length > 0 && (
              <div className="table-wrap" tabIndex={0} role="region" aria-label="Evidence against criteria">
                <table>
                  <caption className="sr-only">Evidence against each criterion, as submitted</caption>
                  <thead>
                    <tr>
                      <th scope="col">Criterion</th>
                      <th scope="col">Files</th>
                      <th scope="col">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.evidenceMapping.map((c, i) => (
                      <tr key={i}>
                        <th scope="row">{c.criterion}</th>
                        <td>{c.evidenceFileIds.map((id) => `#${id}`).join(', ') || 'None'}</td>
                        <td>{CRITERION_RESULT[c.result] ?? c.result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {d.aiDraft && d.aiDraft.doesNotSupport && d.aiDraft.doesNotSupport.length > 0 && (
            <Card title="Does not support the Fully in place status">
              <DoesNotSupportList items={d.aiDraft.doesNotSupport} heading={false} />
              <p className="muted small">From the assistant's draft, with the reason. The firm chose the status above.</p>
            </Card>
          )}

          {d.aiDraft && (
            <details className="card">
              <summary>
                <strong>The assistant's original draft</strong> <span className="muted small">({d.aiModel}), before the firm's edits</span>
              </summary>
              <p>
                {statusText(d.aiDraft.proposedStatus)}, maturity {d.aiDraft.proposedMaturity}. <span className="muted small">{d.aiDraft.maturityReasoning}</span>
              </p>
              {d.aiDraft.promptInjectionNoticed && <p className="notice small">The assistant reported text in the evidence that tried to instruct it.</p>}
              <p className="pre">{d.aiDraft.implementationSummary}</p>
              {d.aiDraft.citations.length > 0 && <p className="muted small">Sources: {d.aiDraft.citations.join(' ')}</p>}
            </details>
          )}

          {d.conversation.length > 0 && (
            <details className="card">
              <summary>
                <strong>Conversation with the assistant</strong> <span className="muted small">({d.conversation.length} messages)</span>
              </summary>
              <ol className="chat">
                {d.conversation.map((m, i) => (
                  <li key={i} className={`bubble ${m.role}`}>
                    <span className="who">{m.role === 'user' ? d.submittedBy : 'Assistant'}</span>
                    <p className="pre">{m.content}</p>
                  </li>
                ))}
              </ol>
            </details>
          )}

          {d.status === 'submitted' && !d.isOwnSubmission && <Decision d={d} />}
          {d.isOwnSubmission && <p className="notice">You made this submission, so another reviewer decides it.</p>}
          {d.reviewedAt && (
            <Card title="Review">
              <p>
                {d.reviewedBy}, {formatDate(d.reviewedAt)}
              </p>
              {d.reviewNote && <p className="pre">{d.reviewNote}</p>}
            </Card>
          )}
        </>
      )}
    </Load>
  );
}

const SCAN_TEXT: Record<string, string> = { pending: 'malware scan in progress', not_scanned: 'not yet scanned for malware', malicious: 'flagged by malware scanning' };

function Decision({ d }: { d: Detail }) {
  const call = useApiClient();
  const home = useQueueHome();
  const navigate = useNavigate();
  // No default: a review records only the decision the reviewer chose (5A bug fix, 2026-10-03).
  const [decision, setDecision] = useState<'accepted' | 'returned' | null>(null);
  const [status, setStatus] = useState(d.proposedStatus);
  const [maturity, setMaturity] = useState<number | ''>(d.proposedMaturity ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!decision) return;
    setBusy(true);
    setError(null);
    try {
      await call(`/review/submissions/${d.id}/decision`, {
        method: 'POST',
        body: { decision, note: note || null, reviewedStatus: decision === 'accepted' ? status : null, reviewedMaturity: decision === 'accepted' && maturity !== '' ? maturity : null },
      });
      navigate(home);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Your review">
      <form onSubmit={submit} className="form">
        <fieldset>
          <legend>Decision</legend>
          <label className="check">
            <input type="radio" name="decision" checked={decision === 'accepted'} onChange={() => setDecision('accepted')} /> Accept: the evidence supports this rating
          </label>
          <label className="check">
            <input type="radio" name="decision" checked={decision === 'returned'} onChange={() => setDecision('returned')} /> Return with questions
          </label>
        </fieldset>
        {decision === 'accepted' && (
          <div className="row">
            <label>
              Status to record
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {(['no_gap', 'partial_gap', 'gap'] as const).map((k) => (
                  <option key={k} value={k}>
                    {resultLabel(k)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Maturity (0 to 5)
              <select value={maturity} onChange={(e) => setMaturity(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">Not rated</option>
                {[0, 1, 2, 3, 4, 5].map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <label>
          {decision === 'returned' ? 'Questions for the firm (required)' : 'Note to the firm (optional)'}
          <textarea rows={4} maxLength={2000} minLength={decision === 'returned' ? 10 : 0} required={decision === 'returned'} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button className="pill" disabled={busy || !decision}>
          {busy ? 'Saving…' : decision === 'accepted' ? 'Accept and record' : decision === 'returned' ? 'Return to the firm' : 'Choose a decision'}
        </button>
        {error && (
          <p role="alert" className="notice">
            {error}
          </p>
        )}
      </form>
    </Card>
  );
}
